import React, { useRef, useEffect, useCallback, useState } from 'react';
import { Stage, Layer, Image as KonvaImage, Circle, Text, Group, Transformer } from 'react-konva';
import { KonvaEventObject } from 'konva/lib/Node';
import { useEditorStore } from '../stores/editorStore';
import type { Hotspot, Point } from '../types';

interface CanvasProps {
  imageUrl: string;
  width: number;
  height: number;
  readOnly?: boolean;
  onHotspotClick?: (id: string) => void;
  onHotspotMouseEnter?: (id: string, event: any) => void;
  onHotspotMouseLeave?: (id: string, event: any) => void;
}

const CONTAINER_PADDING = 40;
const MIN_STAGE_SIZE = 400;

const Canvas: React.FC<CanvasProps> = ({
  imageUrl,
  width,
  height,
  readOnly = false,
  onHotspotClick,
  onHotspotMouseEnter,
  onHotspotMouseLeave
}) => {
  const stageRef = useRef<any>(null);
  const imageRef = useRef<any>(null);
  const transformerRef = useRef<any>(null);

  const {
    project,
    zoom,
    pan,
    selectedElementId,
    setZoom,
    setPan,
    selectElement,
    addHotspot,
    updateElement,
    deleteElement,
  } = useEditorStore();

  const [imageObj, setImageObj] = useState<HTMLImageElement | null>(null);
  const [stageSize, setStageSize] = useState({ width: width + CONTAINER_PADDING * 2, height: height + CONTAINER_PADDING * 2 });
  const [isDragging, setIsDragging] = useState(false);

  // Load image
  useEffect(() => {
    if (!imageUrl) {
      setImageObj(null);
      return;
    }

    const img = new window.Image();
    // Only set crossOrigin for external URLs, not for data URLs (local files)
    if (!imageUrl.startsWith('data:')) {
      img.crossOrigin = 'anonymous';
    }
    img.onload = () => {
      setImageObj(img);
    };
    img.onerror = (err) => {
      console.error('Errore caricamento immagine:', err);
      setImageObj(null);
    };
    img.src = imageUrl;
  }, [imageUrl]);

  // Update stage size when image dimensions change
  useEffect(() => {
    setStageSize({
      width: Math.max(MIN_STAGE_SIZE, width + CONTAINER_PADDING * 2),
      height: Math.max(MIN_STAGE_SIZE, height + CONTAINER_PADDING * 2),
    });
  }, [width, height]);

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ignore key events if typing in an input or textarea
      const target = e.target as HTMLElement;
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable
      ) {
        return;
      }

      if (e.key === 'Delete' || e.key === 'Backspace') {
        if (selectedElementId) {
          deleteElement(selectedElementId);
        }
      }
      if (e.key === 'Escape') {
        selectElement(null);
      }
      if ((e.ctrlKey || e.metaKey) && e.key === 'z') {
        e.preventDefault();
        if (e.shiftKey) {
          // Redo
        } else {
          // Undo
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedElementId, deleteElement, selectElement]);

  // Zoom with wheel
  const handleWheel = useCallback((e: KonvaEventObject<WheelEvent>) => {
    e.evt.preventDefault();
    const stage = stageRef.current;
    if (!stage) return;

    const oldScale = stage.scaleX();
    const pointer = stage.getPointerPosition();
    if (!pointer) return;

    const scaleBy = 1.1;
    const newScale = e.evt.deltaY > 0 ? oldScale / scaleBy : oldScale * scaleBy;

    // Limit zoom
    const clampedScale = Math.max(0.1, Math.min(5, newScale));
    setZoom(clampedScale);

    // Calculate new position to zoom towards pointer
    const mousePointTo = {
      x: (pointer.x - stage.x()) / oldScale,
      y: (pointer.y - stage.y()) / oldScale,
    };

    const newPos = {
      x: pointer.x - mousePointTo.x * clampedScale,
      y: pointer.y - mousePointTo.y * clampedScale,
    };

    setPan(newPos);
  }, [setZoom, setPan]);

  // Add hotspot on double click
  const handleStageDblClick = useCallback((e: KonvaEventObject<MouseEvent>) => {
    const stage = stageRef.current;
    if (!stage) return;

    const pos = stage.getRelativePointerPosition();
    if (!pos) return;

    // Check if clicking on empty area
    const clickedOnEmpty = e.target === stage;
    if (!clickedOnEmpty) return;

    const hotspotId = addHotspot(
      { x: pos.x, y: pos.y },
      { x: pos.x - 25, y: pos.y - 25, width: 50, height: 50 }
    );

    if (hotspotId) {
      selectElement(hotspotId);
    }
  }, [addHotspot, selectElement]);

  // Handle stage click for deselection
  const handleStageClick = useCallback((e: KonvaEventObject<MouseEvent>) => {
    const clickedOnEmpty = e.target === stageRef.current;
    if (clickedOnEmpty) {
      selectElement(null);
    }
  }, [selectElement]);

  // Handle drag end for pan
  const handleDragEnd = useCallback((e: KonvaEventObject<DragEvent>) => {
    // Only update pan if the target is the Stage itself (not a hotspot or other element)
    if (e.target !== stageRef.current) return;
    setPan({
      x: e.target.x(),
      y: e.target.y(),
    });
  }, [setPan]);

  // Update transformer when selection changes
  useEffect(() => {
    const transformer = transformerRef.current;
    const stage = stageRef.current;
    if (!transformer || !stage) return;

    const selectedNode = stage.findOne(`#${selectedElementId}`);
    if (selectedNode) {
      transformer.nodes([selectedNode]);
      transformer.getLayer()?.batchDraw();
    } else {
      transformer.nodes([]);
      transformer.getLayer()?.batchDraw();
    }
  }, [selectedElementId]);

  // Calculate position for hotspot markers (center of bounding box)
  const getHotspotPosition = (hotspot: Hotspot): Point => {
    return {
      x: hotspot.boundingBox.x + hotspot.boundingBox.width / 2,
      y: hotspot.boundingBox.y + hotspot.boundingBox.height / 2,
    };
  };

  return (
    <div className="relative w-full h-full overflow-hidden bg-gray-100">
      {/* Message when no image is loaded */}
      {!imageObj && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="text-center text-gray-400">
            <svg className="w-16 h-16 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <p className="text-lg font-medium">Nessuna immagine caricata</p>
            <p className="text-sm mt-1">Trascina un file qui o usa il pulsante Upload</p>
          </div>
        </div>
      )}
      <Stage
        ref={stageRef}
        width={stageSize.width}
        height={stageSize.height}
        scaleX={zoom}
        scaleY={zoom}
        x={pan.x}
        y={pan.y}
        draggable={!isDragging}
        onWheel={handleWheel}
        onDblClick={handleStageDblClick}
        onClick={handleStageClick}
        onDragEnd={handleDragEnd}
        className="cursor-grab active:cursor-grabbing"
      >
        <Layer>
          {/* Background image */}
          {imageObj && (
            <KonvaImage
              ref={imageRef}
              image={imageObj}
              x={CONTAINER_PADDING}
              y={CONTAINER_PADDING}
              width={width}
              height={height}
              listening={false}
            />
          )}

          {/* Hotspots */}
          {project?.elements.map((element) => {
            if (element.type !== 'hotspot') return null;
            const hotspot = element as Hotspot;
            const pos = getHotspotPosition(hotspot);
            const isSelected = selectedElementId === hotspot.id;

            // Visibility logic
            // In editor: show invisible hotspots with reduced opacity/dashed line
            // In readOnly: show normally if visible, completely transparent hit area if invisible

            // If readOnly and NOT visible, we render a hit-area only (transparent circle)
            if (readOnly && !hotspot.isVisible) {
              return (
                <Group
                  key={hotspot.id}
                  id={hotspot.id}
                  x={pos.x}
                  y={pos.y}
                  onClick={(e) => {
                    e.cancelBubble = true;
                    if (onHotspotClick) onHotspotClick(hotspot.id);
                  }}
                  onMouseEnter={(e) => {
                    const container = e.target.getStage()?.container();
                    if (container) container.style.cursor = 'pointer';
                    onHotspotMouseEnter?.(hotspot.id, e);
                  }}
                  onMouseLeave={(e) => {
                    const container = e.target.getStage()?.container();
                    if (container) container.style.cursor = 'default';
                    onHotspotMouseLeave?.(hotspot.id, e);
                  }}
                >
                  <Circle
                    radius={25}
                    fill="transparent"
                  />
                </Group>
              );
            }

            // Normal rendering (Editor or readOnly+visible)
            const isEditorInvisible = !readOnly && !hotspot.isVisible;

            return (
              <Group
                key={hotspot.id}
                id={hotspot.id}
                x={pos.x}
                y={pos.y}
                draggable={!readOnly}
                onClick={(e) => {
                  e.cancelBubble = true;
                  if (readOnly) {
                    if (onHotspotClick) onHotspotClick(hotspot.id);
                  } else {
                    selectElement(hotspot.id);
                  }
                }}
                onDragStart={(e) => {
                  if (readOnly) return;
                  e.cancelBubble = true;
                  setIsDragging(true);
                }}
                onDragEnd={(e) => {
                  if (readOnly) return;
                  e.cancelBubble = true;
                  setIsDragging(false);
                  const newPos = { x: e.target.x(), y: e.target.y() };
                  updateElement(hotspot.id, {
                    position: newPos,
                    boundingBox: {
                      x: newPos.x - 25,
                      y: newPos.y - 25,
                      width: 50,
                      height: 50,
                    },
                  });
                }}
                onMouseEnter={(e) => {
                  if (readOnly) {
                    const container = e.target.getStage()?.container();
                    if (container) container.style.cursor = 'pointer';
                    onHotspotMouseEnter?.(hotspot.id, e);
                  }
                }}
                onMouseLeave={(e) => {
                  if (readOnly) {
                    const container = e.target.getStage()?.container();
                    if (container) container.style.cursor = 'default';
                    onHotspotMouseLeave?.(hotspot.id, e);
                  }
                }}
              >
                {/* Content based on iconType */}
                {hotspot.iconType === 'emoji' && hotspot.iconContent ? (
                  <Text
                    text={hotspot.iconContent}
                    fontSize={40}
                    x={-20}
                    y={-20}
                    opacity={isEditorInvisible ? 0.7 : 1}
                  />
                ) : hotspot.iconType === 'image' && hotspot.iconContent ? (
                  <Group>
                    <Circle
                      radius={25}
                      fill="white"
                      stroke={isSelected ? '#0ea5e9' : '#fff'}
                      strokeWidth={isSelected ? 3 : 0}
                      shadowColor="black"
                      shadowBlur={10}
                      shadowOpacity={0.3}
                      opacity={isEditorInvisible ? 0.7 : 1}
                    />
                    <KonvaImage
                      image={(() => {
                        const img = new window.Image();
                        img.src = hotspot.iconContent || '';
                        return img;
                      })()}
                      width={40}
                      height={40}
                      x={-20}
                      y={-20}
                      cornerRadius={20}
                    />
                  </Group>
                ) : (
                  // Default Icon
                  <Group>
                    <Circle
                      radius={25}
                      fill={isSelected ? '#0ea5e9' : (isEditorInvisible ? 'rgba(239, 68, 68, 0.3)' : '#ef4444')}
                      stroke={isEditorInvisible ? 'rgba(255,255,255,0.5)' : '#ffffff'}
                      strokeWidth={3}
                      dash={isEditorInvisible ? [4, 4] : undefined}
                      shadowColor="black"
                      shadowBlur={10}
                      shadowOpacity={0.3}
                      opacity={isEditorInvisible ? 0.7 : 0.9}
                    />
                    <Text
                      text="+"
                      fontSize={24}
                      fill={isEditorInvisible ? 'rgba(255,255,255,0.7)' : 'white'}
                      x={-8}
                      y={-12}
                      fontStyle="bold"
                    />
                  </Group>
                )}
              </Group>
            );
          })}

          {/* Transformer for selected element */}
          <Transformer
            ref={transformerRef}
            boundBoxFunc={(oldBox, newBox) => {
              // Limit minimum size
              if (newBox.width < 20 || newBox.height < 20) {
                return oldBox;
              }
              return newBox;
            }}
          />
        </Layer>
      </Stage>

      {/* Zoom controls */}
      <div className="absolute bottom-4 right-4 flex flex-col gap-2 bg-white rounded-lg shadow-lg p-2">
        <button
          onClick={() => setZoom(Math.min(5, zoom * 1.2))}
          className="p-2 hover:bg-gray-100 rounded-lg text-gray-700"
          title="Zoom in"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
        </button>
        <button
          onClick={() => setZoom(Math.max(0.1, zoom / 1.2))}
          className="p-2 hover:bg-gray-100 rounded-lg text-gray-700"
          title="Zoom out"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
          </svg>
        </button>
        <button
          onClick={() => {
            setZoom(1);
            setPan({ x: 0, y: 0 });
          }}
          className="p-2 hover:bg-gray-100 rounded-lg text-gray-700"
          title="Reset view"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
          </svg>
        </button>
      </div>

      {/* Zoom percentage */}
      <div className="absolute bottom-4 left-4 bg-white rounded-lg shadow-lg px-3 py-2 text-sm text-gray-600">
        {Math.round(zoom * 100)}%
      </div>
    </div>
  );
};

export default Canvas;
